import React from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';

import {
  DocExportLocalUploadRow,
  LocalUploadTargetType,
  normalizeLocalUploadItems,
  normalizeLocalUploadTotalCount,
  useBatchIngestDocExportLocalTargetsMutation,
  useCancelDocExportLocalUploadMutation,
  useFetchDocExportLocalUploads,
  useProcessPendingDocExportLocalFilesMutation,
  useUploadDocExportLocalFilesMutation,
} from '../../../../hooks/useDocExportLocalFiles';

import { dateLabel, statusChip } from './ragAdminUtils';
import { CiscoProductSelector, GtmSelector } from './RagSelects';
import LocalFileUploadDialog from './LocalFileUploadDialog';

function productIdOf(row: any): number | null {
  const value =
    row?.product_id ??
    row?.product?.id ??
    (typeof row?.product === 'number' ? row.product : null);

  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function gtmIdOf(row: any): number | null {
  const value =
    row?.go_to_market_id ??
    row?.go_to_market?.id ??
    (typeof row?.go_to_market === 'number' ? row.go_to_market : null);

  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function targetTypeOf(row: any): LocalUploadTargetType {
  const raw = String(row?.target_type || '').toLowerCase();

  if (raw === 'go_to_market') return 'go_to_market';

  if (row?.go_to_market_id || row?.go_to_market_name || row?.go_to_market) {
    return 'go_to_market';
  }

  return 'product';
}

function statusOf(row: any): string {
  return String(row?.status || '').trim().toLowerCase();
}

function isFailedRetryable(row: DocExportLocalUploadRow): boolean {
  return statusOf(row) === 'failed' && Boolean(row.retryable);
}

function canProcessRow(row: DocExportLocalUploadRow): boolean {
  const status = statusOf(row);
  return status === 'pending' || isFailedRetryable(row);
}

function canCancelRow(row: DocExportLocalUploadRow): boolean {
  const status = statusOf(row);

  return (
    status === 'pending' ||
    status === 'skipped_duplicate' ||
    isFailedRetryable(row)
  );
}

function cancelDisabledReason(row: DocExportLocalUploadRow): string {
  const status = statusOf(row);

  if (canCancelRow(row)) {
    return 'Remove this upload before it is processed';
  }

  if (status === 'processing') {
    return 'This upload is currently processing and cannot be removed.';
  }

  if (status === 'converted') {
    return 'This upload has already been converted and cannot be removed here.';
  }

  if (status === 'cancelled') {
    return 'This upload has already been cancelled.';
  }

  return `This upload cannot be removed from status "${row.status}".`;
}

function sizeLabel(size?: number | null) {
  if (size === undefined || size === null || Number.isNaN(Number(size))) return '-';

  const numeric = Number(size);
  if (numeric < 1024) return `${numeric} B`;
  if (numeric < 1024 * 1024) return `${Math.round(numeric / 1024)} KB`;
  return `${Math.round(numeric / (1024 * 1024))} MB`;
}

function s3Uri(bucket?: string | null, key?: string | null) {
  if (!bucket || !key) return '-';
  return `s3://${bucket}/${key}`;
}

function rowTitle(row: DocExportLocalUploadRow) {
  return row.title || row.original_filename || row.upload_id;
}

function rowTargetLabel(row: DocExportLocalUploadRow) {
  if (targetTypeOf(row) === 'go_to_market') {
    return (
      row.go_to_market_name ||
      (typeof row.go_to_market === 'object' ? row.go_to_market?.name : null) ||
      `GTM ${gtmIdOf(row) || '-'}`
    );
  }

  return (
    row.product_name ||
    (typeof row.product === 'object' ? row.product?.name : null) ||
    `Product ${productIdOf(row) || '-'}`
  );
}

function contentS3KeyOf(row: DocExportLocalUploadRow): string | null {
  return (
    row.conversion_payload?.content_s3_key ||
    row.conversion_payload?.markdown_s3_key ||
    row.markdown_s3_key ||
    null
  );
}

function contentFormatOf(row: DocExportLocalUploadRow): string {
  return (
    row.conversion_payload?.content_format ||
    (row.conversion_payload?.passthrough ? 'json' : '') ||
    (row.markdown_s3_key ? 'markdown' : '')
  );
}

function uniqueProductIds(rows: DocExportLocalUploadRow[]) {
  const seen = new Set<number>();
  const out: number[] = [];

  rows.forEach((row) => {
    const id = productIdOf(row);
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  });

  return out;
}

function uniqueGtmIds(rows: DocExportLocalUploadRow[]) {
  const seen = new Set<number>();
  const out: number[] = [];

  rows.forEach((row) => {
    const id = gtmIdOf(row);
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  });

  return out;
}

const LocalFilesPanel: React.FC = () => {
  const [page, setPage] = React.useState(0);
  const [size, setSize] = React.useState(25);
  const [statusFilter, setStatusFilter] = React.useState('');
  const [targetTypeFilter, setTargetTypeFilter] = React.useState<'all' | LocalUploadTargetType>('all');
  const [productIdFilter, setProductIdFilter] = React.useState<number | null>(null);
  const [gtmIdFilter, setGtmIdFilter] = React.useState<number | null>(null);
  const [search, setSearch] = React.useState('');
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [lastActionResult, setLastActionResult] = React.useState<any>(null);

  const params = React.useMemo(() => {
    const out: Record<string, any> = {
      page: page + 1,
      size,
      detailed: true,
      depth: 2,
      'order-by': '-created',
    };

    if (statusFilter) out.status = statusFilter;

    if (targetTypeFilter !== 'all') {
      out.target_type = targetTypeFilter;
    }

    if (productIdFilter) out.product = productIdFilter;
    if (gtmIdFilter) out.go_to_market = gtmIdFilter;
    if (search.trim()) out.search = search.trim();

    return out;
  }, [gtmIdFilter, page, productIdFilter, search, size, statusFilter, targetTypeFilter]);

  const query = useFetchDocExportLocalUploads(params, {
    placeholderData: keepPreviousData,
    refetchInterval: 30000,
  });

  const uploadMutation = useUploadDocExportLocalFilesMutation();
  const processMutation = useProcessPendingDocExportLocalFilesMutation();
  const ingestMutation = useBatchIngestDocExportLocalTargetsMutation();
  const cancelMutation = useCancelDocExportLocalUploadMutation();

  const rows = normalizeLocalUploadItems<DocExportLocalUploadRow>(query.data);
  const total = normalizeLocalUploadTotalCount(query.data, rows);

  const busy =
    query.isLoading ||
    query.isFetching ||
    uploadMutation.isPending ||
    processMutation.isPending ||
    ingestMutation.isPending ||
    cancelMutation.isPending;

  const scopedRows = React.useMemo(() => {
    return rows.filter((row) => {
      if (targetTypeFilter !== 'all' && targetTypeOf(row) !== targetTypeFilter) return false;
      if (productIdFilter && productIdOf(row) !== productIdFilter) return false;
      if (gtmIdFilter && gtmIdOf(row) !== gtmIdFilter) return false;
      return true;
    });
  }, [gtmIdFilter, productIdFilter, rows, targetTypeFilter]);

  const convertedRows = rows.filter((row) => statusOf(row) === 'converted');

  const convertedProductIds = React.useMemo(() => uniqueProductIds(convertedRows), [convertedRows]);
  const convertedGtmIds = React.useMemo(() => uniqueGtmIds(convertedRows), [convertedRows]);

  const pendingCount = rows.filter((row) => statusOf(row) === 'pending').length;
  const processingCount = rows.filter((row) => statusOf(row) === 'processing').length;
  const convertedCount = convertedRows.length;
  const failedCount = rows.filter((row) => statusOf(row) === 'failed').length;
  const cancelledCount = rows.filter((row) => statusOf(row) === 'cancelled').length;

  const refresh = async () => {
    await query.refetch();
  };

  const resetTargetFilters = (targetType: 'all' | LocalUploadTargetType) => {
    setTargetTypeFilter(targetType);
    setProductIdFilter(null);
    setGtmIdFilter(null);
    setPage(0);
  };

  const processPendingForScope = async (
    {
      targetType,
      productId,
      gtmId,
      includeFailedRetryable = false,
    }: {
      targetType: 'all' | LocalUploadTargetType;
      productId?: number | null;
      gtmId?: number | null;
      includeFailedRetryable?: boolean;
    },
  ) => {
    setLastActionResult(null);

    const result = await processMutation.mutateAsync({
      target_type: targetType,
      product_ids: productId ? [productId] : null,
      go_to_market_ids: gtmId ? [gtmId] : null,
      limit: 100,
      include_failed_retryable: includeFailedRetryable,
      continue_on_error: true,
    });

    setLastActionResult(result);
    await refresh();
  };

  const processPending = async (includeFailedRetryable = false) => {
    await processPendingForScope({
      targetType: targetTypeFilter,
      productId: productIdFilter,
      gtmId: gtmIdFilter,
      includeFailedRetryable,
    });
  };

  const cancelUpload = async (row: DocExportLocalUploadRow) => {
    if (!canCancelRow(row)) return;

    const confirmed = window.confirm(
      [
        'Remove this local upload before processing?',
        '',
        `File: ${row.original_filename || row.upload_id}`,
        `Upload ID: ${row.upload_id}`,
        `Status: ${row.status}`,
        '',
        'This will mark the upload as cancelled and attempt to delete the pending S3 object.',
      ].join('\n'),
    );

    if (!confirmed) return;

    setLastActionResult(null);

    const result = await cancelMutation.mutateAsync({
      upload_id: row.upload_id,
      delete_s3_object: true,
    });

    setLastActionResult(result);
    await refresh();
  };

  const batchIngest = async () => {
    const productIds =
      productIdFilter
        ? [productIdFilter]
        : targetTypeFilter === 'go_to_market'
          ? []
          : convertedProductIds.length
            ? convertedProductIds
            : uniqueProductIds(scopedRows);

    const gtmIds =
      gtmIdFilter
        ? [gtmIdFilter]
        : targetTypeFilter === 'product'
          ? []
          : convertedGtmIds.length
            ? convertedGtmIds
            : uniqueGtmIds(scopedRows);

    if (!productIds.length && !gtmIds.length) {
      window.alert('No product or Go-To-Market IDs are available to ingest.');
      return;
    }

    const confirmed = window.confirm(
      [
        'Run batch RAG ingest?',
        '',
        productIds.length ? `Products: ${productIds.join(', ')}` : '',
        gtmIds.length ? `Go-To-Markets: ${gtmIds.join(', ')}` : '',
      ].filter(Boolean).join('\n'),
    );

    if (!confirmed) return;

    setLastActionResult(null);

    const result = await ingestMutation.mutateAsync({
      product_ids: productIds,
      go_to_market_ids: gtmIds,
      purpose: 'doc_export',
      include_skipped_documents: false,
      chunk_size: 20,
      wait_timeout_seconds: 14400,
      poll_seconds: 15.0,
      overwrite_option: null,
    });

    setLastActionResult(result);
  };

  const uploadFiles = async (payload: any) => {
    const result = await uploadMutation.mutateAsync(payload);
    await refresh();
    return result;
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'center' }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Local File Management
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Upload local PDFs, Word docs, PowerPoints, Excel files, CSVs, Markdown, text, HTML, and JSON for Cisco
              products or Go-To-Markets. Raw files are stored under <b>local-uploads/pending</b>. Processing converts
              supported files to markdown/assets, passes JSON through without conversion, updates canonical manifests,
              and the normal RAG batch ingest picks them up.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={refresh}
              disabled={busy}
            >
              Refresh
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setUploadOpen(true)}
              disabled={uploadMutation.isPending}
            >
              Upload Files
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {query.error ? (
        <Alert severity="error">
          {(query.error as Error)?.message || 'Failed to load local uploads.'}
        </Alert>
      ) : null}

      {uploadMutation.error ? (
        <Alert severity="error">{uploadMutation.error.message || 'Upload failed.'}</Alert>
      ) : null}

      {processMutation.error ? (
        <Alert severity="error">{processMutation.error.message || 'Processing failed.'}</Alert>
      ) : null}

      {ingestMutation.error ? (
        <Alert severity="error">{ingestMutation.error.message || 'Batch ingest failed.'}</Alert>
      ) : null}

      {cancelMutation.error ? (
        <Alert severity="error">{cancelMutation.error.message || 'Cancel failed.'}</Alert>
      ) : null}

      {busy ? <LinearProgress /> : null}

      <Grid container spacing={2}>
        <Grid item xs={12} md={2.4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Pending</Typography>
            <Typography variant="h5" fontWeight={700}>{pendingCount}</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={2.4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Processing</Typography>
            <Typography variant="h5" fontWeight={700}>{processingCount}</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={2.4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Converted</Typography>
            <Typography variant="h5" fontWeight={700}>{convertedCount}</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={2.4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Failed</Typography>
            <Typography variant="h5" fontWeight={700}>{failedCount}</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={2.4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Cancelled</Typography>
            <Typography variant="h5" fontWeight={700}>{cancelledCount}</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3} lg={2}>
            <TextField
              fullWidth
              select
              label="Target Type"
              value={targetTypeFilter}
              onChange={(event) => resetTargetFilters(event.target.value as any)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="product">Products</MenuItem>
              <MenuItem value="go_to_market">Go-To-Markets</MenuItem>
            </TextField>
          </Grid>

          {targetTypeFilter !== 'go_to_market' ? (
            <Grid item xs={12} lg={4}>
              <CiscoProductSelector
                value={productIdFilter}
                onChange={(value) => {
                  setProductIdFilter(value);
                  setGtmIdFilter(null);
                  setPage(0);
                }}
                label="Filter Product"
                helperText="Optional. Process/ingest actions are scoped to this product when selected."
              />
            </Grid>
          ) : null}

          {targetTypeFilter !== 'product' ? (
            <Grid item xs={12} lg={4}>
              <GtmSelector
                value={gtmIdFilter}
                onChange={(value) => {
                  setGtmIdFilter(value);
                  setProductIdFilter(null);
                  setPage(0);
                }}
                label="Filter Go-To-Market"
                helperText="Optional. Process/ingest actions are scoped to this GTM when selected."
              />
            </Grid>
          ) : null}

          <Grid item xs={12} md={4} lg={2}>
            <TextField
              fullWidth
              select
              label="Status"
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">pending</MenuItem>
              <MenuItem value="processing">processing</MenuItem>
              <MenuItem value="converted">converted</MenuItem>
              <MenuItem value="failed">failed</MenuItem>
              <MenuItem value="skipped_duplicate">skipped_duplicate</MenuItem>
              <MenuItem value="cancelled">cancelled</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={8} lg={3}>
            <TextField
              fullWidth
              label="Search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
              placeholder="Filename, upload ID, title..."
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<PlayArrowIcon />}
                onClick={() => processPending(false)}
                disabled={processMutation.isPending}
              >
                Process Pending
              </Button>

              <Button
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={batchIngest}
                disabled={ingestMutation.isPending}
              >
                Batch Ingest
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {lastActionResult ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Last Action Result
          </Typography>

          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.25,
              maxHeight: 360,
              overflow: 'auto',
              bgcolor: 'grey.50',
              borderRadius: 1,
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {JSON.stringify(lastActionResult, null, 2)}
          </Box>
        </Paper>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={2} flexWrap="wrap" useFlexGap>
          <Typography variant="h6" fontWeight={700}>
            Local Uploads
          </Typography>

          <Chip size="small" variant="outlined" label={`${total} total`} />
          <Chip size="small" variant="outlined" label={`${rows.length} loaded`} />
        </Stack>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 1480 }}>
            <TableHead>
              <TableRow>
                <TableCell>File</TableCell>
                <TableCell width={130}>Status</TableCell>
                <TableCell width={150}>Target</TableCell>
                <TableCell width={240}>Product / GTM</TableCell>
                <TableCell>Raw Upload S3</TableCell>
                <TableCell>Converted / Manifest</TableCell>
                <TableCell width={120}>Size</TableCell>
                <TableCell width={180}>Updated</TableCell>
                <TableCell width={120} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((row) => {
                const rowTargetType = targetTypeOf(row);
                const rowProductId = productIdOf(row);
                const rowGtmId = gtmIdOf(row);
                const rowStatus = statusOf(row);
                const contentFormat = contentFormatOf(row);
                const contentS3Key = contentS3KeyOf(row);

                return (
                  <TableRow key={row.id || row.upload_id} hover>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography variant="body2" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
                          {rowTitle(row)}
                        </Typography>

                        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                          {row.original_filename}
                        </Typography>

                        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                          Upload ID: {row.upload_id}
                        </Typography>

                        {row.conversion_payload?.passthrough ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            color="info"
                            label="JSON passthrough"
                            sx={{ alignSelf: 'flex-start', mt: 0.25 }}
                          />
                        ) : null}

                        {row.error_message ? (
                          <Typography variant="caption" color="error" sx={{ wordBreak: 'break-word' }}>
                            {row.error_type ? `${row.error_type}: ` : ''}
                            {row.error_message}
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>

                    <TableCell>{statusChip(row.status)}</TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={rowTargetType === 'go_to_market' ? 'secondary' : 'primary'}
                        label={rowTargetType === 'go_to_market' ? 'GTM' : 'Product'}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>
                        {rowTargetLabel(row)}
                      </Typography>

                      {rowTargetType === 'go_to_market' ? (
                        <Typography variant="caption" color="text.secondary">
                          ID: {rowGtmId || '-'} / Type: {row.go_to_market_type || '-'}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          ID: {rowProductId || '-'} / Version: {row.product_version || row.product_recommended_version || '-'}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                        {s3Uri(row.upload_bucket, row.upload_s3_key)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Stack spacing={0.75}>
                        <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                          {contentFormat ? `${contentFormat}: ` : 'Content: '}
                          {s3Uri(row.output_bucket, contentS3Key)}
                        </Typography>

                        <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                          Manifest: {s3Uri(row.output_bucket, row.manifest_s3_key)}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell>{sizeLabel(row.original_size_bytes)}</TableCell>

                    <TableCell>
                      <Typography variant="caption">
                        {dateLabel(row.last_updated || row.completed_at || row.created)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Process pending/failed retryable uploads for this target">
                          <span>
                            <IconButton
                              size="small"
                              disabled={!canProcessRow(row) || processMutation.isPending || cancelMutation.isPending}
                              onClick={() => {
                                if (rowTargetType === 'go_to_market') {
                                  if (!rowGtmId) return;

                                  setTargetTypeFilter('go_to_market');
                                  setGtmIdFilter(rowGtmId);
                                  setProductIdFilter(null);

                                  void processPendingForScope({
                                    targetType: 'go_to_market',
                                    gtmId: rowGtmId,
                                    includeFailedRetryable: rowStatus === 'failed',
                                  });
                                  return;
                                }

                                if (!rowProductId) return;

                                setTargetTypeFilter('product');
                                setProductIdFilter(rowProductId);
                                setGtmIdFilter(null);

                                void processPendingForScope({
                                  targetType: 'product',
                                  productId: rowProductId,
                                  includeFailedRetryable: rowStatus === 'failed',
                                });
                              }}
                            >
                              <PlayArrowIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title={cancelDisabledReason(row)}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={!canCancelRow(row) || cancelMutation.isPending || processMutation.isPending}
                              onClick={() => void cancelUpload(row)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        {!rows.length && !query.isLoading ? (
          <Typography variant="body2" color="text.secondary" mt={2}>
            No local uploads found.
          </Typography>
        ) : null}

        <TablePagination
          component="div"
          count={Number(total || 0)}
          page={page}
          rowsPerPage={size}
          onPageChange={(_, next) => setPage(next)}
          onRowsPerPageChange={(event) => {
            setSize(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <LocalFileUploadDialog
        open={uploadOpen}
        uploading={uploadMutation.isPending}
        onClose={() => setUploadOpen(false)}
        onUpload={uploadFiles}
      />
    </Stack>
  );
};

export default LocalFilesPanel;